import React, { useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { selectNodes, selectEdges } from '../../../../redux/slices/adsOptimizationSlice';
import CustomNode from './CustomNode';

// Simple shallow comparison for node data (skip position during drag)
const shallowCompareNodeData = (prevData, nextData) => {
  if (prevData === nextData) return true;
  if (!prevData || !nextData) return false;
  
  // Compare key fields only, skip deep comparison
  return prevData.nodeType === nextData.nodeType &&
         prevData.label === nextData.label &&
         prevData.customName === nextData.customName &&
         prevData.isProcessing === nextData.isProcessing &&
         prevData.isCompleted === nextData.isCompleted &&
         prevData.hasError === nextData.hasError &&
         prevData.isDebugTarget === nextData.isDebugTarget &&
         prevData.isDebugPaused === nextData.isDebugPaused;
};

// Memoized selector - simplified without expensive JSON.stringify
const selectMemoizedNodes = createSelector(
  [selectNodes],
  (nodes) => nodes
);

const selectMemoizedEdges = createSelector(
  [selectEdges],
  (edges) => edges
);

// Memoized component - optimized comparison without JSON.stringify
const MemoizedCustomNode = React.memo(CustomNode, (prevProps, nextProps) => {
  // Fast path: same reference
  if (prevProps === nextProps) return true;
  
  // Compare essential props only (skip position - handled by ReactFlow)
  return prevProps.id === nextProps.id &&
         prevProps.selected === nextProps.selected &&
         shallowCompareNodeData(prevProps.data, nextProps.data);
});

// Hook để lấy memoized flow data - simplified to avoid unnecessary transforms
export const useMemoizedFlowData = () => {
  const nodes = useSelector(selectMemoizedNodes);
  const edges = useSelector(selectMemoizedEdges);
  
  // Return directly without transformation - nodes already have correct structure
  return React.useMemo(() => ({ nodes, edges }), [nodes, edges]);
};

// Throttle utility for high-frequency updates
export const useThrottledCallback = (callback, delay = 16) => {
  const lastCallRef = useRef(0);
  const pendingArgsRef = useRef(null);
  const rafIdRef = useRef(null);
  
  return useCallback((...args) => {
    const now = performance.now();
    const timeSinceLastCall = now - lastCallRef.current;
    
    if (timeSinceLastCall >= delay) {
      // Enough time has passed, execute immediately
      lastCallRef.current = now;
      callback(...args);
    } else {
      // Store args and schedule execution
      pendingArgsRef.current = args;
      
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          if (pendingArgsRef.current) {
            lastCallRef.current = performance.now();
            callback(...pendingArgsRef.current);
            pendingArgsRef.current = null;
          }
        });
      }
    }
  }, [callback, delay]);
};

// Performance monitoring hook - disabled in production
export const usePerformanceMonitor = () => {
  const nodes = useSelector(selectNodes);
  const edges = useSelector(selectEdges);
  const lastLogRef = useRef(0);
  
  React.useEffect(() => {
    // Only log in development
    if (process.env.NODE_ENV !== 'development') return;
    
    const now = performance.now();
    // Throttle logs to max once per second
    if (now - lastLogRef.current > 1000) {
      lastLogRef.current = now;
      console.log(`[Performance] Flow: ${nodes.length} nodes, ${edges.length} edges`);
    }
  }, [nodes.length, edges.length]);
};

export { MemoizedCustomNode, selectMemoizedNodes, selectMemoizedEdges };